import { Entity, Column, PrimaryGeneratedColumn, ManyToOne, JoinColumn, Index, OneToMany } from 'typeorm';
import { Project } from '../project/project.entity';
import { EncodeJob, PublicEncodeJob } from '../encode/encode-job.entity';
import { AssetDto } from '../asset/models/asset.dto';
import { IsOptional } from 'class-validator';
import { LocaleDto } from '../creative-unit-package/models/locale.dto';
import { Utils } from '../project/project.utils';
import { ProjectCommentDto } from '../project/models/project-comment.dto';

export const DEFAULT_CART_ITEM_NAME = '';

export enum CartItemType {
	AD_UNIT = 'creativeUnit',
	// AD_UNIT_OPTIMIZER = 'creativeUnitOptimizer',
	VIDEO = 'video',
	HTML_UNIT = 'html'
}

export enum CartItemReviewStatus {
	APPROVED = 'approved',
	REJECTED = 'rejected',
	PENDING = 'pending'
}

/**
 * Overview of review status counts for a cart item, used to quickly determine overall review status without having to fetch all reviews
 */
export class CartItemReviewOverview {
	approved: number;
	rejected: number;
	pending: number;
}

export type PublicProjectCartItem = Pick<
	ProjectCartItem,
	| 'id'
	| 'name'
	| 'type'
	| 'projectId'
	| 'itemData'
	| 'metadata'
	| 'locale'
	| 'approved'
	| 'acceptedCreativePackageTermsDate'
	| 'completedWizard'
	| 'reviewStatus'
	| 'reviews'
> & {
	encodeJobId?: string;
	encodeJob?: PublicEncodeJob;
	sourceId?: string;
	isVersion?: boolean;
	versionNumber?: number;
	versions?: PublicProjectCartItem[];
	modified?: string;
	latestPublishedVersionId?: string;
};

@Entity('projectsCartItem')
@Index('idx_cart_item_data_package_id', { synchronize: false })
@Index('IDX_projectsCartItem_metadataPackageId', ['metadataPackageId'])
@Index('IDX_projectsCartItem_metadataListingId', ['metadataListingId'])
export class ProjectCartItem {
	constructor(value?: Partial<ProjectCartItem>) {
		for (const k in value) {
			this[k] = value[k];
		}
	}

	@PrimaryGeneratedColumn('uuid')
	public id: string;

	@Column({
		type: 'text',
		default: DEFAULT_CART_ITEM_NAME,
		nullable: false
	})
	public name?: string;

	@Column({
		type: 'enum',
		enum: CartItemType,
		default: CartItemType.AD_UNIT,
		nullable: false
	})
	public type: CartItemType;

	@Column('jsonb', { nullable: true })
	public itemData: any;

	@Column('uuid')
	@Index()
	public projectId: string;

	@ManyToOne(
		type => Project,
		project => project.cartItems,
		{
			eager: false,
			onDelete: 'CASCADE',
			nullable: false
		}
	)
	@JoinColumn({ name: 'projectId' })
	public project?: Project | Partial<Project>;

	@Column('uuid', { nullable: true })
	encodeJobId?: string;

	@ManyToOne(() => EncodeJob, {
		eager: true,
		orphanedRowAction: 'delete',
		onDelete: 'CASCADE'
	})
	@JoinColumn({ name: 'encodeJobId' })
	encodeJob?: EncodeJob;

	@Column('jsonb', { nullable: true })
	metadata?: Record<string, any>;

	// Generated (STORED) extractions of the two `metadata` keys the reporting
	// queries join on. `metadata` is a large jsonb (1.1 GB table on ShopX);
	// evaluating `metadata->>'packageId'` per candidate row inside an OR-join ran
	// 1.5M times for one Template Listings request. Postgres maintains these on
	// write; never written from the app (insert/update false), not loaded by
	// default (select false).
	@Column({
		type: 'text',
		nullable: true,
		asExpression: `"metadata"->>'packageId'`,
		generatedType: 'STORED',
		insert: false,
		update: false,
		select: false
	})
	metadataPackageId?: string;

	@Column({
		type: 'text',
		nullable: true,
		asExpression: `"metadata"->>'listingId'`,
		generatedType: 'STORED',
		insert: false,
		update: false,
		select: false
	})
	metadataListingId?: string;

	@Column('jsonb', { nullable: true })
	source?: any;

	@Column('jsonb', { nullable: true })
	public thumbnail: AssetDto | null;

	@IsOptional()
	@Column('jsonb', { nullable: true })
	locale?: LocaleDto;

	@Column('boolean', { default: false })
	public deleted: boolean;

	@Column({ type: 'timestamptz', default: () => 'NOW()' })
	public created: string;

	@Column({ type: 'timestamptz', default: () => 'NOW()' })
	public modified: string;

	@Column('boolean', { nullable: true })
	public approved?: boolean;

	@Column('text', { nullable: true })
	public errorMessage?: string;

	@Column('timestamptz', { nullable: true })
	public acceptedCreativePackageTermsDate?: string;

	@Column('boolean', { nullable: true })
	public completedWizard?: boolean;

	@Column('uuid', { nullable: true })
	public sourceId?: string; // original cart item id from which this is a version

	@OneToMany(
		() => ProjectCartItem,
		cartItem => cartItem.sourceId
	)
	public versions?: ProjectCartItem[];

	@Column('integer', { nullable: true })
	public versionNumber?: number; // version number to track versions of the same cart item

	@IsOptional()
	@Column('jsonb', { nullable: true })
	reviewStatus?: CartItemReviewOverview;

	// virtual field to hold reviews when fetching a cart item with its reviews in one query - not persisted in the database
	reviews?: ProjectCommentDto[];

	/** Latest published package id for this item's version group (set by cart queries; not persisted). */
	latestPublishedVersionId?: string;

	public toPublic(dehydratedObject?: boolean, exclude?: Array<keyof PublicProjectCartItem>) {
		const pub: Partial<PublicProjectCartItem> = {
			id: this.id,
			name: this.name,
			type: this.type,
			projectId: this.projectId,
			metadata: this._buildMetadata(),
			approved: this.approved,
			locale: this.locale,
			acceptedCreativePackageTermsDate: this.acceptedCreativePackageTermsDate,
			completedWizard: this.completedWizard,
			versionNumber: this.versionNumber,
			isVersion: !!this.versionNumber && this.versionNumber > 0,
			sourceId: this.sourceId,
			encodeJobId: this.encodeJobId,
			modified: this.modified
		};

		if (this.encodeJob && !exclude?.includes('encodeJob')) {
			pub.encodeJob = new EncodeJob(this.encodeJob).toPublic();
		}

		pub.itemData = dehydratedObject ? {} : this.itemData;

		pub.reviewStatus = this.reviewStatus;
		pub.reviews = this.reviews;

		return pub as PublicProjectCartItem;
	}

	public _buildMetadata(): Record<string, any> {
		const metadata = Utils.dehydrateCartItemMetadata(this.metadata);
		const videoMetadata = Utils.getVideoMetadata(this);

		return {
			...Utils.getCartItemMetadata(this),
			...metadata,
			...videoMetadata,
			...(this.latestPublishedVersionId ? { latestPublishedVersionId: this.latestPublishedVersionId } : {})
		};
	}
}
